import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import { Dialog } from '@/components/Base/Headless';
import { deleteStaff, fetchStaffDuitNowQrBlob, fetchStaffList, type StaffRow } from '@/api/staff';

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatStatus(staff: StaffRow): { label: string; className: string } {
  if (!staff.isActive) {
    return {
      label: 'Deactivated',
      className: 'bg-slate-100 text-slate-600',
    };
  }
  if (!staff.isRegistered) {
    return {
      label: 'Pending',
      className: 'bg-amber-100 text-amber-700',
    };
  }
  return {
    label: 'Active',
    className: 'bg-emerald-100 text-emerald-700',
  };
}

function DuitNowQrCell({ staffId, hasDuitNowQr }: { staffId: string; hasDuitNowQr: boolean }) {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(hasDuitNowQr);

  useEffect(() => {
    if (!hasDuitNowQr) {
      setSrc(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let objectUrl: string | undefined;
    setLoading(true);

    void fetchStaffDuitNowQrBlob(staffId)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [staffId, hasDuitNowQr]);

  if (loading) {
    return <Lucide icon="Loader" className="w-4 h-4 animate-spin text-slate-400" />;
  }

  if (!src) {
    return <span className="text-slate-400">No Image</span>;
  }

  return (
    <img
      src={src}
      alt="DuitNow QR"
      className="h-12 w-12 rounded-md border border-slate-200 bg-white object-contain p-0.5 dark:border-darkmode-400"
    />
  );
}

function Main() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<StaffRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await fetchStaffList();
      setStaff(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ambassadors');
      setStaff([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const openDeleteDialog = (row: StaffRow) => {
    setDeleteError('');
    setDeleteTarget(row);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    setDeleteError('');
    try {
      await deleteStaff(deleteTarget.id);
      setDeleteTarget(null);
      await fetchStaff();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete ambassador');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
              Ambassadors
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Manage and view all internal ambassadors
            </p>
          </div>
          <Button
            type="button"
            variant="primary"
            className="w-full sm:w-auto font-semibold uppercase tracking-wide"
            onClick={() => navigate('/ambassador/add')}
          >
            <Lucide icon="Plus" className="stroke-[1.3] w-4 h-4 mr-2" />
            Add Ambassador
          </Button>
        </div>
      </div>

      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 shadow-sm overflow-hidden">
        {error && (
          <div className="mx-6 mt-5 px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200/80 dark:border-darkmode-400 bg-slate-50/80 dark:bg-darkmode-400/30">
                <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Ambassador
                </th>
                <th className="px-4 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Membership Code
                </th>
                <th className="px-4 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  DuitNow QR
                </th>
                <th className="px-4 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Region
                </th>
                <th className="px-4 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  State
                </th>
                <th className="px-4 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  QR Shared
                </th>
                <th className="px-4 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Surveys
                </th>
                <th className="px-4 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Points
                </th>
                <th className="px-4 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Status
                </th>
                <th className="px-4 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500 text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center text-slate-400">
                    <Lucide icon="Loader" className="w-5 h-5 mx-auto mb-2 animate-spin" />
                    Loading ambassadors...
                  </td>
                </tr>
              ) : staff.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center text-slate-400">
                    No ambassadors found
                  </td>
                </tr>
              ) : (
                staff.map((row) => {
                  const status = formatStatus(row);
                  return (
                    <tr
                      key={row.id}
                      className="border-b border-slate-100 dark:border-darkmode-400 last:border-b-0 hover:bg-slate-50/60 dark:hover:bg-darkmode-400/20"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                            {getInitials(row.name)}
                          </div>
                          <button
                            type="button"
                            className="font-medium text-slate-800 dark:text-slate-100 hover:text-primary text-left transition-colors"
                            onClick={() =>
                              navigate(`/ambassador/${row.id}`, {
                                state: { ambassadorName: row.name },
                              })
                            }
                          >
                            {row.name}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-slate-600 dark:text-slate-300">
                        {row.memberCode || '—'}
                      </td>
                      <td className="px-4 py-4">
                        <DuitNowQrCell staffId={row.id} hasDuitNowQr={Boolean(row.hasDuitNowQr)} />
                      </td>
                      <td className="px-4 py-4 text-slate-600 dark:text-slate-300">
                        —
                      </td>
                      <td className="px-4 py-4 text-slate-600 dark:text-slate-300">
                        —
                      </td>
                      <td className="px-4 py-4 text-slate-600 dark:text-slate-300 tabular-nums">
                        {row.qrSharedCount}
                      </td>
                      <td className="px-4 py-4 text-slate-600 dark:text-slate-300 tabular-nums">
                        {row.surveyCount}
                      </td>
                      <td className="px-4 py-4 text-slate-600 dark:text-slate-300 tabular-nums">
                        —
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${status.className}`}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <div className="inline-flex items-center justify-center gap-1">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-primary transition-colors"
                            aria-label={`Edit ${row.name}`}
                            onClick={() => navigate(`/ambassador/${row.id}/edit`)}
                          >
                            <Lucide icon="Pencil" className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-md p-2 text-slate-400 hover:bg-danger/10 hover:text-danger transition-colors"
                            aria-label={`Delete ${row.name}`}
                            onClick={() => openDeleteDialog(row)}
                          >
                            <Lucide icon="Trash2" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && staff.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-slate-200/80 dark:border-darkmode-400 px-6 py-4 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between">
            <span>
              Showing 1 to {staff.length} of {staff.length} entries
            </span>
          </div>
        )}
      </div>

      <Dialog open={deleteTarget !== null} onClose={closeDeleteDialog} size="sm">
        <Dialog.Panel>
          <Dialog.Title>
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">
              Delete Ambassador
            </h3>
          </Dialog.Title>
          <Dialog.Description>
            <p className="text-slate-600 dark:text-slate-300">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-slate-800 dark:text-slate-100">
                {deleteTarget?.name}
              </span>
              ?
            </p>
            <p className="mt-3 text-sm text-danger">
              This action cannot be reversed. The ambassador will be removed from the system along
              with all linked QR scan records and survey submissions.
            </p>
            {deleteError && (
              <div className="mt-4 px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
                {deleteError}
              </div>
            )}
          </Dialog.Description>
          <Dialog.Footer className="flex justify-end gap-2">
            <Button type="button" variant="outline-primary" onClick={closeDeleteDialog} disabled={deleting}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={() => void handleConfirmDelete()}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />
                  Deleting…
                </>
              ) : (
                'Confirm Deletion'
              )}
            </Button>
          </Dialog.Footer>
        </Dialog.Panel>
      </Dialog>
    </div>
  );
}

export default Main;
